import { t } from "i18next";
import Joi from "joi";
import {
  MAX_TEXT_FIELD_SIZE,
  PHONE_REGEX,
  MAX_FILE_SIZE,
  EMAIL_REGEX,
} from "src/resources/constant";
import {
  calculateFileSizeFromBase64,
  isAllowedFileExtension,
} from "src/utils/helpers";

export const contractSchema = (contractData: any) => {
  return Joi.object({
    amcContractNumber: Joi.string()
      .required()
      .max(MAX_TEXT_FIELD_SIZE)
      .messages({
        "any.required": `${t("amcContractNumberRequired")}`,
        "string.empty": `${t("amcContractNumberRequired")}`,
      }),
    contractTypeId: Joi.any().optional().allow("").allow(null),
    startDate: Joi.date()
      .required()
      .messages({
        "any.required": `${t("startDateValidation")}`,
        "date.base": `${t("startDateValidation")}`,
      }),
    endDate: Joi.date()
      .required()
      .messages({
        "any.required": `${t("endDateValidation")}`,
        "string.empty": `${t("endDateValidation")}`,
        "date.base": `${t("endDateValidation")}`,
      }),
    civilDefenseContractApprovalStatusId: Joi.number()
      .integer()
      .min(1)
      .required()
      .messages({
        "any.required": `${t("cdApprovalStatusValidation")}`,
        "number.base": `${t("cdApprovalStatusValidation")}`,
        "number.min": `${t("cdApprovalStatusValidation")}`,
        "number.integer": `${t("cdApprovalStatusValidation")}`,
      }),

    amcCopy: Joi.any()
      .custom((value, helpers) => {
        const fileSize = contractData.amcCopy?.fileBase64
          ? calculateFileSizeFromBase64(contractData.amcCopy.fileBase64)
          : 0;
        const fileExtension = contractData.amcCopy?.fileName
          ?.split(".")
          .pop()
          .toLowerCase();

        if (fileSize > MAX_FILE_SIZE && contractData?.amcCopy?.fileName) {
          return helpers.error("amcCopy.maxSize");
        }
        if (fileExtension && !isAllowedFileExtension(fileExtension)) {
          return helpers.error("any.invalid");
        }

        return value;
      })
      .messages({
        "amcCopy.maxSize": `${t("fileSizeNote")}`,
        "any.invalid": `${t("fileTypeNote")}`,
      }),
    engineerTechnicianName: Joi.string()
      .required()
      .messages({
        "any.required": `${t("engineerTechnicianValidation")}`,
        "string.empty": `${t("engineerTechnicianValidation")}`,
      }),

    technicianMobileNumber: Joi.string()
      .pattern(new RegExp(PHONE_REGEX))
      .required()
      .messages({
        "any.required": `${t("technicianMobileValidation")}`,
        "string.empty": `${t("technicianMobileValidation")}`,
        "string.pattern.base": `${t("mobileNumberPatternValidation")}`,
      }),

    managerMobileNumber: Joi.string()
      .pattern(new RegExp(PHONE_REGEX))
      .required()
      .messages({
        "any.required": `${t("managerMobileValidation")}`,
        "string.empty": `${t("managerMobileValidation")}`,
        "string.pattern.base": `${t("mobileNumberPatternValidation")}`,
      }),

    technicianAlternateNumber: Joi.string()
      .pattern(new RegExp(PHONE_REGEX))
      .optional()
      .allow(null)
      .allow("")
      .messages({
        "string.pattern.base": `${t("mobileNumberPatternValidation")}`,
      }),

    managerAlternateNumber: Joi.string()
      .pattern(new RegExp(PHONE_REGEX))
      .optional()
      .allow(null)
      .allow("")
      .messages({
        "string.pattern.base": `${t("mobileNumberPatternValidation")}`,
      }),

    technicianEmail: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .pattern(new RegExp(EMAIL_REGEX))
      .messages({
        "any.required": `${t("emailValidation")}`,
        "string.empty": `${t("emailValidation")}`,
        "string.email": `${t("emailValidation")}`,
        "string.pattern.base": t("emailPatternValidation"),
      }),

    managerEmail: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .pattern(new RegExp(EMAIL_REGEX))
      .messages({
        "any.required": `${t("emailValidation")}`,
        "string.empty": `${t("emailValidation")}`,
        "string.email": `${t("emailValidation")}`,
        "string.pattern.base": t("emailPatternValidation"),
      }),

    managerSupervisorName: Joi.string()
      .required()
      .messages({
        "any.required": `${t("managerValidation")}`,
        "string.empty": `${t("managerValidation")}`,
      }),

    preventiveDates: Joi.any().optional().allow("").allow(null),

    statusId: Joi.any().optional().allow("").allow(null),

    id: Joi.any().optional().allow("").allow(null),

    contractSystemServicesInfo: Joi.any().optional().allow("").allow(null),

    isNew: Joi.any().optional().allow("").allow(null),

    contractId: Joi.any().optional().allow("").allow(null),

    bmcCompanyID: Joi.any().optional().allow("").allow(null),

    propertyGuid: Joi.any().optional().allow("").allow(null),
    
    statusCode: Joi.any().optional().allow("").allow(null),
  });
};
